import * as React from 'react';
import { DeleteAppPayload } from './DeleteAppPage';
import { CenterFormOrientation, CenterFormWidget } from '../../../widgets/layout/CenterFormWidget';
import { FormLayoutSection, FormLayoutWidget } from '../../../widgets/layout/FormLayoutWidget';
import { GroupedButtons } from '../../../widgets/buttons/GroupedButtons';
import { ParagraphWidget } from '../../../widgets/forms/ParagraphWidget';
import { FormikContext, useFormik } from 'formik';
import { styled } from '../../../theme';
import { FormikInputWidget } from '../../../widgets/inputs/FormikInput';
import { AppModel } from '@journeyapps-platform/lib-journey-core';
import { Keys, useKeyPress } from '../../../hooks/useKeyPress';

export interface DeleteAppConfirmWidgetProps {
  payload: Partial<DeleteAppPayload>;
  app: AppModel;
  next: () => any;
  onCancel: () => any;
}

export const DeleteAppConfirmWidget: React.FC<DeleteAppConfirmWidgetProps> = (props) => {
  const formik = useFormik<{ appName?: string }>({
    initialValues: {},
    validateOnBlur: false,
    validate: (values) => {
      const error: { appName?: string } = {};
      if (!values.appName) {
        error['appName'] = 'This is required';
      }

      if (`${values.appName}`.trim() !== `${props.app.model.name}`.trim()) {
        error['appName'] = 'App name does not match';
      }
      return error;
    },
    onSubmit: async (values) => {
      await props.next();
    }
  });
  useKeyPress(Keys.ENTER, {
    onKeyPressed: () => {
      formik.submitForm();
    }
  });

  const getContent = (): FormLayoutSection => {
    return {
      key: 'confirm_delete',
      elements: [
        <S.Container>
          <S.Paragraph>
            <b>Re-enter {props.app.model.name}</b>
          </S.Paragraph>
          <S.SubText>Please enter it exactly as it appears above</S.SubText>
          <S.StyledInput
            name="appName"
            error={formik.errors.appName}
            input={{
              autoFocus: true,
              placeholder: `Re-enter ${props.app.model.name}`
            }}
          />
        </S.Container>
      ]
    };
  };

  const getButtons = () => {
    return {
      key: 'submit',
      elements: [
        <GroupedButtons
          btns={[
            {
              label: 'Cancel',
              action: () => {
                props.onCancel();
              }
            },
            {
              label: 'Delete App',
              disabled: !formik.values.appName,
              action: async (event, loading) => {
                loading(true);
                await formik.submitForm();
                loading(false);
              }
            }
          ]}
        />
      ]
    };
  };

  return (
    <FormikContext.Provider value={formik}>
      <CenterFormWidget
        noLogo={true}
        primaryLabel={`Confirm deletion of ${props.app.model.name}`}
        orientation={CenterFormOrientation.ROW}
      >
        <FormLayoutWidget sections={[getContent(), getButtons()]} />
      </CenterFormWidget>
    </FormikContext.Provider>
  );
};

const BASE_LABEL = 'delete-app-confirm';
namespace S {
  export const Container = styled.div`
    label: ${BASE_LABEL};
  `;

  export const StyledInput = styled(FormikInputWidget)`
    label: ${BASE_LABEL}__input;
    max-width: 280px;
  `;

  export const Paragraph = styled(ParagraphWidget)`
    label: ${BASE_LABEL}__paragraph;
    text-align: left;
    width: 408px;
    user-select: text;
  `;

  export const SubText = styled.div`
    label: ${BASE_LABEL}__sub-text;
    font-size: 12px;
    color: ${(p) => p.theme.text.secondary};
    padding-bottom: 10px;
  `;
}
